<script setup>
import { onMounted, ref, computed, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ContactMultiSelect from '../components/ContactMultiSelect.vue';
import ConflictDialog from '../components/ConflictDialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();

const businessAccounts = useMapGetter(
  'businessAccounts/getBusinessAccountsList'
);
const uiFlags = useMapGetter('businessAccounts/getUIFlags');
const meta = useMapGetter('businessAccounts/getMeta');

const activeTab = ref('accounts');
const pageNumber = computed(() => Number(route.query?.page) || 1);

const searchQuery = ref('');
const searchTimeout = ref(null);
const searchInputRef = ref(null);

const tabs = [
  {
    id: 'contacts',
    label: t('CONTACTS_LAYOUT.TABS.CONTACTS'),
    route: 'contacts_dashboard_index',
  },
  {
    id: 'accounts',
    label: t('CONTACTS_LAYOUT.TABS.ACCOUNTS'),
    route: 'accounts_dashboard_index',
  },
];

const handleTabClick = tab => {
  activeTab.value = tab.id;
  if (tab.route) {
    router.push(accountScopedRoute(tab.route));
  }
};

const onTabChange = index => {
  const selectedTab = tabs[index];
  if (selectedTab) {
    handleTabClick(selectedTab);
  }
};

const isLoading = computed(() => uiFlags.value.isFetching);

const dialogRef = ref(null);
const conflictDialogRef = ref(null);

const formData = ref({
  name: '',
  domain: '',
  contactIds: [],
});

const selectedContacts = ref([]);
const contactMultiSelectRef = ref(null);
const selectedContactIds = ref([]);
const conflicts = ref([]);
const pendingContactIds = ref([]);
const pendingBusinessAccountId = ref(null);
const contactDecisions = ref({});

const updatePageParam = page => {
  const query = {
    ...route.query,
    page: page.toString(),
  };
  router.replace({ query });
};

const fetchBusinessAccounts = async (page = 1) => {
  try {
    const params = { page };

    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim();
    }

    await store.dispatch('businessAccounts/get', params);
    updatePageParam(page);
  } catch (error) {
    console.error('Erro ao carregar business accounts:', error);
  }
};

const handlePageChange = page => {
  fetchBusinessAccounts(page);
};

const handleSearch = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(async () => {
    await fetchBusinessAccounts(1);
    await nextTick();

    if (searchInputRef.value && searchInputRef.value.$el) {
      const inputElement = searchInputRef.value.$el.querySelector('input');
      if (inputElement) inputElement.focus();
    }
  }, 500);
};

const openAddAccountModal = () => {
  dialogRef.value?.open();
  if (contactMultiSelectRef.value) {
    contactMultiSelectRef.value.loadInitialContacts();
  }
};

const closeAddAccountModal = () => {
  dialogRef.value?.close();
  resetForm();
};

const resetForm = () => {
  formData.value = {
    name: '',
    domain: '',
    contactIds: [],
  };
  selectedContacts.value = [];
  selectedContactIds.value = [];
  conflicts.value = [];
  pendingContactIds.value = [];
  pendingBusinessAccountId.value = null;
  contactDecisions.value = {};
};

const handleContactSelect = contact => {
  const index = selectedContacts.value.findIndex(c => c.id === contact.id);
  if (index > -1) {
    selectedContacts.value.splice(index, 1);
  } else {
    selectedContacts.value.push(contact);
  }
  selectedContactIds.value = selectedContacts.value.map(c => c.id);
};

const handleContactRemove = contact => {
  const index = selectedContacts.value.findIndex(c => c.id === contact.id);
  if (index > -1) {
    selectedContacts.value.splice(index, 1);
    selectedContactIds.value = selectedContacts.value.map(c => c.id);
  }
};

const getSelectedContacts = () => {
  return selectedContacts.value;
};

const saveAccount = async (force = false) => {
  try {
    const businessAccountData = {
      business_account: {
        name: formData.value.name,
        domain: formData.value.domain,
      },
      contact_ids: selectedContactIds.value,
    };

    if (Object.keys(contactDecisions.value).length > 0) {
      businessAccountData.contact_decisions = contactDecisions.value;
    }

    const response = await store.dispatch(
      'businessAccounts/create',
      businessAccountData
    );

    if (response?.conflicts && response.conflicts.length > 0) {
      conflicts.value = response.conflicts;
      pendingContactIds.value = selectedContactIds.value;
      pendingBusinessAccountId.value = response.id;
      conflictDialogRef.value?.open();
      return;
    }

    resetForm();
    dialogRef.value?.close();

    await fetchBusinessAccounts();
  } catch (error) {
    console.error('Erro ao salvar conta:', error);
  }
};

const handleApplyDecisions = async () => {
  try {
    await store.dispatch('businessAccounts/linkContacts', {
      id: pendingBusinessAccountId.value,
      contactIds: pendingContactIds.value,
      contactDecisions: contactDecisions.value,
    });

    resetForm();
    dialogRef.value?.close();
    contactDecisions.value = {};

    await fetchBusinessAccounts();
  } catch (error) {
    console.error('Erro ao salvar conta:', error);
  }
};

const handleCancelConflict = () => {
  conflicts.value = [];
  pendingContactIds.value = [];
  pendingBusinessAccountId.value = null;
  contactDecisions.value = {};

  fetchBusinessAccounts();
};

const viewAccountDetails = accountId => {
  router.push({
    name: 'account_details',
    params: { businessAccountId: accountId },
  });
};

const selectedBusinessAccount = ref(null);
const showDeleteBusinessAccountModal = ref(false);
const loading = ref({});

const openDeletePopup = businessAccount => {
  showDeleteBusinessAccountModal.value = true;
  selectedBusinessAccount.value = businessAccount;
};

const closeDeleteBusinessAccountModal = () => {
  showDeleteBusinessAccountModal.value = false;
  selectedBusinessAccount.value = null;
};

const deleteBusinessAccount = async id => {
  loading.value[id] = true;
  try {
    await store.dispatch('businessAccounts/delete', id);
    useAlert(t('CONTACTS_LAYOUT.DELETE.API.SUCCESS_MESSAGE'));
    await fetchBusinessAccounts();
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const confirmDeletion = () => {
  if (selectedBusinessAccount.value) {
    deleteBusinessAccount(selectedBusinessAccount.value.id);
    closeDeleteBusinessAccountModal();
  }
};

const deleteMessage = computed(() => {
  return selectedBusinessAccount.value
    ? ` ${selectedBusinessAccount.value.name}`
    : '';
});

onMounted(() => {
  fetchBusinessAccounts(pageNumber.value);
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <section
      class="flex w-full h-full gap-4 overflow-hidden justify-evenly bg-n-background"
    >
      <div class="flex flex-col w-full h-full transition-all duration-300">
        <header class="sticky top-0 z-10">
          <div
            class="flex items-start sm:items-center justify-between w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
          >
            <span class="text-xl font-medium truncate text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.TABS.ACCOUNTS') }}
            </span>
            <div
              class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4"
            >
              <div class="flex items-center gap-4">
                <div class="w-80">
                  <Input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    :placeholder="
                      t('BUSINESS_ACCOUNTS.INDEX.SEARCH_PLACEHOLDER')
                    "
                    :disabled="isLoading"
                    :custom-input-class="[
                      'h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-10 !py-2 rtl:!pr-10',
                    ]"
                    @input="handleSearch"
                  >
                    <template #prefix>
                      <Icon
                        icon="i-lucide-search"
                        class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-3 rtl:right-3"
                      />
                    </template>
                  </Input>
                </div>
                <Button
                  :label="t('BUSINESS_ACCOUNTS.INDEX.ADD_ACCOUNT')"
                  color="blue"
                  size="sm"
                  @click="openAddAccountModal"
                />
              </div>
            </div>
          </div>
        </header>

        <main class="flex-1 overflow-y-auto">
          <div class="w-full mx-auto max-w-[60rem] px-6 py-6">
            <div
              v-if="isLoading"
              class="flex items-center justify-center py-10 text-n-slate-11"
            >
              <Spinner />
            </div>
            <div
              v-else-if="businessAccounts.length > 0"
              class="flex flex-col gap-4 pb-6"
            >
              <div
                v-for="businessAccount in businessAccounts"
                :key="businessAccount.id"
                class="border border-n-weak rounded-lg p-4 hover:shadow-sm transition-shadow bg-n-solid-2"
              >
                <div class="flex items-center justify-between flex-1 gap-4">
                  <div class="flex items-center gap-4 flex-1">
                    <Avatar :name="businessAccount.name" :size="48" />
                    <div class="flex flex-col gap-0.5 flex-1">
                      <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span
                          class="text-base font-medium truncate text-n-slate-12"
                        >
                          {{ businessAccount.name }}
                        </span>
                      </div>
                      <div
                        class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
                      >
                        <div class="truncate max-w-72">
                          <span class="text-sm text-n-slate-11">
                            {{ businessAccount.domain }}
                          </span>
                          -
                          <span class="text-sm text-n-slate-11">
                            {{
                              t('BUSINESS_ACCOUNTS.INDEX.CONTACTS_ASSOCIATED', {
                                count: businessAccount.contacts.length,
                              })
                            }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    class="flex items-center gap-2 px-4 py-2 text-sm text-s-500 hover:text-s-600 border border-s-200 rounded-lg hover:bg-s-50 transition-colors"
                    @click="viewAccountDetails(businessAccount.id)"
                  >
                    {{ t('BUSINESS_ACCOUNTS.INDEX.VIEW_DETAILS') }}
                  </button>
                  <Button
                    label=""
                    faded
                    ruby
                    xl
                    icon="i-lucide-trash-2"
                    :is-loading="loading[businessAccount.id]"
                    @click="openDeletePopup(businessAccount)"
                  />
                </div>
              </div>
            </div>

            <div v-else class="text-center py-12">
              <div
                class="w-16 h-16 bg-n-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-8 h-8 text-n-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 class="text-lg font-medium text-n-900 mb-2">
                {{ t('BUSINESS_ACCOUNTS.INDEX.EMPTY_STATE.TITLE') }}
              </h3>
              <p class="text-n-600 mb-4">
                {{ t('BUSINESS_ACCOUNTS.INDEX.EMPTY_STATE.SUBTITLE') }}
              </p>
              <Button
                class="px-4 py-2 bg-s-500 text-white rounded-lg hover:bg-s-600 transition-colors"
                :label="t('BUSINESS_ACCOUNTS.INDEX.EMPTY_STATE.BUTTON')"
                color="blue"
                size="lg"
                @click="openAddAccountModal"
              />
            </div>
          </div>
        </main>

        <footer
          v-if="!isLoading && businessAccounts.length > 0"
          class="sticky bottom-0 z-0 px-4 pb-4"
        >
          <PaginationFooter
            current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
            :current-page="meta.currentPage || 1"
            :total-items="meta.totalItems || 0"
            :items-per-page="meta.itemsPerPage || 15"
            @update:current-page="handlePageChange"
          />
        </footer>
      </div>
    </section>

    <woot-delete-modal
      v-model:show="showDeleteBusinessAccountModal"
      :on-close="closeDeleteBusinessAccountModal"
      :on-confirm="confirmDeletion"
      :title="t('CONTACTS_LAYOUT.DELETE.CONFIRM.TITLE')"
      :message="t('CONTACTS_LAYOUT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="t('CONTACTS_LAYOUT.DELETE.CONFIRM.YES')"
      :reject-text="t('CONTACTS_LAYOUT.DELETE.CONFIRM.NO')"
    />

    <Dialog ref="dialogRef" width="3xl">
      <div class="flex flex-col gap-4">
        <span class="py-1 text-sm font-medium text-n-slate-12">
          {{ t('ACCOUNT_MODAL.TITLE') }}
        </span>
        <div class="flex flex-col items-start gap-2">
          <span class="py-1 text-sm font-medium text-n-slate-12">
            {{ t('ACCOUNT_MODAL.FORM.ACCOUNT_NAME') }} *
          </span>
          <Input
            v-model="formData.name"
            :placeholder="t('ACCOUNT_MODAL.FORM.ACCOUNT_NAME_PLACEHOLDER')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
          />
        </div>

        <div class="flex flex-col items-start gap-2">
          <span class="py-1 text-sm font-medium text-n-slate-12">
            {{ t('ACCOUNT_MODAL.FORM.DOMAIN') }}
          </span>
          <Input
            v-model="formData.domain"
            :placeholder="t('ACCOUNT_MODAL.FORM.DOMAIN_PLACEHOLDER')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            class="w-full"
          />
        </div>

        <div class="flex flex-col items-start gap-2">
          <span class="py-1 text-sm font-medium text-n-slate-12">
            {{ t('ACCOUNT_MODAL.FORM.SEARCH_CONTACTS') }}
          </span>
          <ContactMultiSelect
            ref="contactMultiSelectRef"
            :selected-items="getSelectedContacts()"
            :multiselector-title="t('ACCOUNT_MODAL.FORM.SELECT_CONTACTS')"
            :multiselector-placeholder="
              t('BUSINESS_ACCOUNTS.INDEX.CONTACT_MULTISELECT_PLACEHOLDER')
            "
            :input-placeholder="
              t('ACCOUNT_MODAL.FORM.SEARCH_CONTACTS_PLACEHOLDER')
            "
            icon="👥"
            @select="handleContactSelect"
            @remove="handleContactRemove"
          />
        </div>
      </div>
      <template #footer>
        <div class="py-3 px-2 flex items-center justify-between w-full gap-4">
          <Button
            :label="t('DIALOG.BUTTONS.CANCEL')"
            variant="link"
            type="button"
            class="h-10 hover:!no-underline hover:text-n-brand"
            @click="closeAddAccountModal"
          />
          <Button
            :label="t('ACCOUNT_MODAL.BUTTONS.SAVE')"
            color="blue"
            :disabled="!formData.name.trim()"
            @click="saveAccount"
          />
        </div>
      </template>
    </Dialog>

    <ConflictDialog
      ref="conflictDialogRef"
      :conflicts="conflicts"
      :contact-decisions="contactDecisions"
      @update:contact-decisions="contactDecisions = $event"
      @apply-decisions="handleApplyDecisions"
      @cancel="handleCancelConflict"
    />
  </div>
</template>
